import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';

interface UseUnsavedChangesOptions {
	/** Whether the form was successfully submitted (bypasses dirty check) */
	isSubmitSuccessful?: boolean;
	/** Whether the form is currently being submitted (bypasses dirty check during submission) */
	isSubmitting?: boolean;
}

/**
 * Hook to prevent unsaved changes loss during navigation or browser close.
 *
 * @param isDirty - Boolean indicating if the form has unsaved changes
 * @param options - Optional configuration including isSubmitSuccessful to bypass dirty check after save
 */
export const useUnsavedChanges = (
	isDirty: boolean,
	options: UseUnsavedChangesOptions = {}
) => {
	const { isSubmitSuccessful = false, isSubmitting = false } = options;
	const { t } = useTranslation('common');
	const { showDialog } = useConfirmationDialog();

	// Track if submit was successful to allow navigation without prompt
	const submitSuccessRef = useRef(false);
	if (isSubmitSuccessful) {
		submitSuccessRef.current = true;
	}

	// Track if we're in the middle of a submission - use ref for immediate updates
	const isSubmittingRef = useRef(false);
	isSubmittingRef.current = isSubmitting;

	// Effective dirty state considers both form dirty, submit success, and submission in progress
	const shouldBlock = isDirty && !submitSuccessRef.current && !isSubmitting;

	// 1. Handle browser close / reload / tab close
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (shouldBlock) {
				e.preventDefault();
				e.returnValue = ''; // Required for some browsers
				return '';
			}
		};

		if (shouldBlock) {
			window.addEventListener('beforeunload', handleBeforeUnload);
		}

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [shouldBlock]);

	// Keep refs updated for the blocker callback to access current values
	const isDirtyRef = useRef(isDirty);
	isDirtyRef.current = isDirty;

	// 2. Handle React Router navigation
	// Use refs in the callback to get the most up-to-date values at navigation time
	const blocker = useBlocker(({ currentLocation, nextLocation }) => {
		const shouldBlockNavigation =
			isDirtyRef.current &&
			!submitSuccessRef.current &&
			!isSubmittingRef.current;
		return (
			shouldBlockNavigation &&
			currentLocation.pathname !== nextLocation.pathname
		);
	});

	useEffect(() => {
		if (blocker.state === 'blocked') {
			const proceed = async () => {
				const confirm = await showDialog(
					t('messages.warning'),
					t('messages.unsaved_changes'),
					{
						confirmLabel: t('buttons.leavePage'),
						confirmVariant: 'default',
						cancelLabel: t('buttons.cancel')
					}
				);

				if (confirm) {
					blocker.proceed();
				} else {
					blocker.reset();
				}
			};

			proceed();
		}
	}, [blocker, showDialog]);
};
